import * as mixpanel from 'mixpanel-browser'
import {NextContextProps, nonFatalAssert, redirectQueryParam} from 'notability-services-common-universal'
import {convertToAnalyticsString, type AnalyticsValue} from 'notability-services-common-frontend'
import {AnalyticsObserver, NextContextProvider, useAnalytics} from 'notability-services-next-frontend'
import React, {useEffect, useMemo, useState} from 'react'

import {ParsedUrlQuery} from 'querystring'
import {SubscribeHomeProps} from '../pages/subscribe'
import {getStripePriceAnalyticsParams} from '../components/SubscriptionCard'
import {useRouter} from 'next/router'
import {useSingular} from '../hooks/useSingular'

function getPageProperties(pathname: string, query: ParsedUrlQuery): Record<string, AnalyticsValue> {
  const routes = pathname.split('/').filter((p) => p.length > 0)
  const pageProperties: Record<string, AnalyticsValue> = {
    'Page Type': 'Unknown'
  }
  const lastRoute = routes[routes.length - 1]
  if (lastRoute) {
    pageProperties['Page Type'] = convertToAnalyticsString(lastRoute)
  } else {
    pageProperties['Page Type'] = 'Login'
  }

  const redirectParam = typeof query[redirectQueryParam] === 'string' ? query[redirectQueryParam] : undefined
  if (redirectParam) {
    pageProperties['Redirect To'] = convertToAnalyticsString(redirectParam)
  }

  return pageProperties
}

/**
 * This component wrapper must be applied to every Page component, along with `sspWithLoginContext`.
 */
export function ComponentWithLoginContext<P>(Component: React.ComponentType<P>) {
  const OuterPage = (props: P & NextContextProps) => {
    nonFatalAssert(!!props._nextContext, 'Next Context Data missing when expected')
    const {userId, experimentGroups, email} = props._nextContext
    const router = useRouter()
    const {analyticsInScope} = useAnalytics()
    const [mixpanelId, setMixpanelId] = useState<string | undefined>()

    useEffect(() => {
      if (analyticsInScope) {
        setMixpanelId(mixpanel.get_distinct_id())
      }
    }, [analyticsInScope])

    useSingular({
      anonymousId: mixpanelId
    })

    const pageProperties = useMemo(
      () => (typeof location === 'undefined' ? {} : getPageProperties(location.pathname, router.query)),
      [router]
    )

    return (
      <NextContextProvider data={props._nextContext}>
        <AnalyticsObserver
          email={email}
          iosAnalyticsId={null}
          userId={userId}
          loggedIn={!userId}
          experimentGroups={experimentGroups}
          pageProperties={pageProperties}
          inSwiftApp={false}
          frontendService={'Login'}
          pagePropertyGenerator={() => {
            const {_nextContext, ...otherProps} = props
            if (router.pathname.endsWith('/subscribe')) {
              const subscribeProps = otherProps as unknown as SubscribeHomeProps
              const chosenPrice = subscribeProps.prices?.find((p) => p.stripeId === router.query.plan)
              if (chosenPrice) {
                return getStripePriceAnalyticsParams(chosenPrice)
              }
            }

            return {}
          }}
        >
          <Component {...props} />
        </AnalyticsObserver>
      </NextContextProvider>
    )
  }

  return OuterPage
}
